import cn from 'classnames';
import {FormikValues, getIn, useFormikContext} from 'formik';
import React from 'react';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  onChangeFormatter?: (value: string) => string;
  onValueChange?: (value: string) => void;
  className?: string;
  maxLength?: number;
  showRequiredStar?: boolean;
}

const FormikContextInputField = ({
  name,
  label,
  required,
  disabled,
  placeholder,
  type = 'text',
  showRequiredStar = true,
  onChangeFormatter,
  onValueChange,
  className = '',
  maxLength,
  ...rest
}: Props) => {
  const {values, touched, errors, setFieldValue, handleBlur, validateField} =
    useFormikContext<FormikValues>();

  const error = getIn(touched, name) && getIn(errors, name);
  const value = getIn(values, name) ?? '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    if (maxLength && val.length > maxLength) return;
    if (onChangeFormatter) val = onChangeFormatter(val);
    setFieldValue(name, val);
    onValueChange?.(val);

    // Validate immediately on change
    if (validateField) {
      validateField(name);
    }
  };

  const handleBlurField = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    // Validate on blur as well
    if (validateField) {
      validateField(name);
    }
  };

  return (
    <div className="w-full mt-2 mb-1">
      <label htmlFor={name} className="block mb-2 text-sm font-medium ">
        {label}{' '}
        {required && showRequiredStar && (
          <span className="ml-1 text-red-600"> * </span>
        )}
      </label>

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlurField}
        className={cn(
          'w-full px-3 py-2.5 pr-10 pl-3 border-[1px] placeholder:text-sm border-gray-400 rounded-lg bg-white appearance-none cursor-pointer transition-colors duration-200 focus:outline-none',
          {
            'border-red-500 focus:ring-red-500': error,
            'bg-gray-100 pointer-events-none opacity-60': disabled,
          },
          className,
        )}
        {...rest}
      />

      {error && (
        <div className="mt-1 text-sm text-red-600">{getIn(errors, name)}</div>
      )}
    </div>
  );
};

export default FormikContextInputField;
